import { Link, useParams } from 'react-router'
import { serverNow, useCountdown } from '../lib/clock'
import { useDraftChannel } from '../state/useDraftChannel'
import LotteryStage from '../components/lottery/LotteryStage'
import type { DraftState } from '../types/protocol'

// The public lottery screen. Everyone in the league opens this URL and watches
// the same draw at the same moment.
//
// Token-free on purpose, exactly like /board/:code: GET /api/drafts/:code is
// public and DraftChannel#subscribed requires no token, so the join code in the
// URL is the whole access story.
//
// It deliberately does NOT go through DraftProvider, which fetches the entire
// player pool unconditionally. This page renders no players, no queue and no
// roster — pulling a few thousand rows onto twelve phones in the sixty seconds
// before a synchronised moment is the wrong thing to be doing with the wifi. A
// `skipPool` prop was the alternative and was rejected: a boolean bolted onto a
// shared component so one caller can opt out of its main job. useDraftChannel
// already returns exactly what this page needs and already owns
// refetch-on-connect and refetch-on-visible.

export type LotteryPageState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  /** No draw has ever been scheduled. */
  | { kind: 'unscheduled' }
  /** Scheduled, still ahead. The stage renders the T-minus itself. */
  | { kind: 'countdown' }
  /** Past zero, anchor not here yet. The sweeper runs at 1 Hz, so this can
   *  legitimately last a second. A state, not an error — it renders as spin-up. */
  | { kind: 'pending' }
  /** Anchor set. Running vs. settled is decided by the seek, inside the stage. */
  | { kind: 'reveal'; started: boolean }
  /** Live or complete and no lottery ever ran — the order was set by hand or by
   *  the old Randomize button. Nothing to animate. */
  | { kind: 'past' }

/**
 * The page's whole state machine, with "now" injected. Pure and exported so it
 * is assertable without a renderer — the same trade countdownFrom and
 * createDraftSync already made here.
 */
export function lotteryPageState(
  draft: DraftState | null,
  loading: boolean,
  error: string | null,
  nowMs: number,
): LotteryPageState {
  if (error) return { kind: 'error', message: error }
  if (loading || !draft) return { kind: 'loading' }
  // The anchor outranks status: by the time someone opens the link the draft may
  // already be under way, and the settled order is still what they came for.
  if (draft.lottery_revealed_at) return { kind: 'reveal', started: draft.status !== 'setup' }
  if (draft.status !== 'setup') return { kind: 'past' }
  if (!draft.lottery_at) return { kind: 'unscheduled' }
  return nowMs < Date.parse(draft.lottery_at) ? { kind: 'countdown' } : { kind: 'pending' }
}

export default function Lottery() {
  const { code = '' } = useParams()
  const { draft, loading, error } = useDraftChannel({ code, teamToken: null })

  // The page has to cross countdown -> pending on its own, and this hook's tick
  // is what re-renders it at the moment the mark passes. Its `expired` flag is
  // precisely that edge; lotteryPageState re-derives it from serverNow().
  useCountdown(draft?.lottery_at ? Date.parse(draft.lottery_at) : null)

  const state = lotteryPageState(draft, loading, error, serverNow())

  switch (state.kind) {
    case 'loading':
      return <Message eyebrow="Draft order lottery" body="Loading…" />
    case 'error':
      return <Message eyebrow="Something went wrong" body={state.message} />
    case 'unscheduled':
      return (
        <Message
          eyebrow="Draft order lottery"
          body="No draw is scheduled yet. The commissioner picks the moment; this page is where it happens."
        />
      )
    case 'past':
      return (
        <Message
          eyebrow="Draft order lottery"
          body="This draft's order was set by hand, so there is no draw to watch."
          to={draft ? `/board/${draft.join_code}` : undefined}
          linkText="Go to the board"
        />
      )
    default:
      return (
        <>
          {state.kind === 'reveal' && state.started && draft ? (
            <p className="fixed top-2 left-1/2 z-10 -translate-x-1/2 font-mono text-xs tracking-wider text-ink-lo">
              The draft has started.{' '}
              <Link to={`/board/${draft.join_code}`} className="text-signal underline-offset-4 hover:underline">
                Go to the board
              </Link>
            </p>
          ) : null}
          {/* Keyed on the join code, not on the draft object. The stage latches
              its anchor and its "already there" set on mount, so a client-side
              navigation from one lottery to another — same route, same element
              position — would otherwise reuse the instance and carry the
              previous draw's refs into the new one. The join code is stable
              across every refetch, so this never restarts a running reveal. */}
          <LotteryStage key={draft?.join_code} draft={draft as DraftState} />
        </>
      )
  }
}

function Message({
  eyebrow,
  body,
  to,
  linkText,
}: {
  eyebrow: string
  body: string
  to?: string
  linkText?: string
}) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      <p className="eyebrow">{eyebrow}</p>
      <p className="mt-3 max-w-md text-ink-lo">{body}</p>
      {to && linkText ? (
        <Link
          to={to}
          className="mt-6 text-sm text-ink-mid underline-offset-4 hover:text-ink-hi hover:underline"
        >
          {linkText}
        </Link>
      ) : null}
    </main>
  )
}
